import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import * as particles from 'pixi-particles';
import * as PIXI from 'pixi.js';
import { Antibody, Magnet, BloodCell, Chained, Chainer, Cilia, Consumer, Cytokine, Dead, Moved, OnScreen, Ownership, Physics, Renderable, Submerged, Transform, WaterTrail, Consumable, Collision, HasSplit } from "../../../shared/components";
import { Chaining, DefaultMembraneColor, RenderEffects, Resolution } from "../../../shared/constants";
import { entityTrace } from "../../../shared/entityTrace";
import { easeOutCubic, lerp, lerp2D } from "../../../shared/interpolation";
import { Colors } from '../palettes/colorPalette';
import { animate } from "../../../shared/systems/animationSystem";
import { addComponent, angleFromUp, assertClient, degToRad, metersToPixels, rotateAround, worldToScreenPosition } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { RenderEngine } from '../rendering/renderEngine';
import { SpriteFactory } from "../rendering/spriteFactory";
import { HighlightType } from "../../../shared/highlightType";
import { render } from "react-dom";

assertClient();

//mouse test
// let mx: number;
// let my: number;
// document.addEventListener("mousemove", (e) => {
// 	mx = e.pageX;
// 	my = e.pageY;
// });

//Todo: Constants
const WaterTrailRadiusMaxScale = 0.93;
const WaterTrailRadiusDropoff = 0.08;
const WaterTrailExpiration = 200;

// RendererSystem
export class RendererSystem extends System {
	private engine: RenderEngine;
	private fullscreenBloodEffect = 0;
	private spriteReferencedForDeathAnimation = new Set<Renderable['sprite']>();
	private game: GameClient;
	explosion!: particles.Emitter;
	// Display area effect sprites
	debugEffect = false;

	//Displacement map usage source: https://markhawkinsdesigns.com/blog/tag/pixijs-v4-displacement-filter-demo/
	private displacementMap: PIXI.Sprite | null = null

	private sceneRoot: PIXI.Container;
	private spriteFactory: SpriteFactory;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
		this.engine = this.game.renderEngine
		this.sceneRoot = this.engine.sceneRoot;
		this.spriteFactory = this.engine.spriteFactory;
	}

	async init() {
		// const displacementMap = new PIXI.Sprite(this.engine.spriteFactory.getTexture("displacementMap"));
		// displacementMap.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT
		// const displacementFilter = new PIXI.filters.DisplacementFilter(displacementMap);
		// //TODO: There are seam-looking things without this, figure out what that is about.
		// displacementFilter.padding = 8;
		// displacementFilter.scale.set(RenderEffects.DisplacementScale);
		// this.engine.sceneRoot.filters = [displacementFilter];
		// //this.engine.sceneRoot.addChild(displacementMap)
		// this.displacementMap = displacementMap;

		this.engine.vignette.apply(); //adds vignette effect
	}


	// This method will get called on every frame by default
	execute(delta: number, time: number) {
		this.engine.graphicsOverlay.clear();
		this.engine.graphicsUnderlay.clear();
		this.engine.graphicsMembrane.clear();


		// Iterate through all the entities on the query
		this.queries.renderables.added?.forEach(entity => {
			//TODO: Probably move all this there
			//this.spriteFactory.setSprite(entity);

			const renderable = entity.getComponent(Renderable);

			//Color sprites
			{
				const texture = this.spriteFactory.getColorMap(renderable.assetLocation);
				const colorSprite = new PIXI.Sprite(texture);
				colorSprite.anchor.set(0.5, 0.5);
				renderable.sprite = colorSprite;

				const isLowerLayer = entity.hasComponent(Cytokine);
				if (isLowerLayer) {
					this.sceneRoot.addChildAt(renderable.sprite, 0);
				}
				else {
					this.sceneRoot.addChild(renderable.sprite);
				}
			}

			//Main depth sprite
			let depthTexture: PIXI.Texture;
			{
				depthTexture = this.spriteFactory.getDepthMap(renderable.depthAssetLocation);
				const depthSprite = new PIXI.Sprite(depthTexture);
				depthSprite.anchor.set(0.5, 0.5);
				renderable.depthSprite = depthSprite;
				this.engine.depthRoot.addChild(depthSprite);
			}

			//Set up membrane sprite for immune cells
			if (entity.hasComponent(Antibody)) {
				depthTexture = this.spriteFactory.getMembraneDepthMap(renderable.membraneAssetLocation);
				const depthSprite = new PIXI.Sprite(depthTexture);
				depthSprite.anchor.set(0.5, 0.5);
				renderable.membrane = depthSprite;
				this.engine.membraneRoot.addChild(depthSprite);
			}
			else if (entity.hasComponent(BloodCell)) {
				// I think using a depth spritesheet here will cause an extra draw call. But it also prevents animation
				depthTexture = this.spriteFactory.getMembraneDepthMap(renderable.membraneAssetLocation);
				const depthSprite = new PIXI.Sprite(depthTexture);
				//depthSprite.tint = 0xff0066;
				depthSprite.anchor.set(0.5, 0.5);
				renderable.membrane = depthSprite;
				this.engine.membraneRoot.addChild(depthSprite);
			}

			//Set up tint/glow sprites
			{
				renderable.glowSprite = new PIXI.Sprite(this.spriteFactory.getGlowMap(renderable.glowAssetLocation));
				renderable.glowSprite.anchor.set(0.5, 0.5);
				//renderable.glowSprite.blendMode = PIXI.BLEND_MODES.ADD;
				renderable.glowSprite.alpha = 0.3;
				renderable.glowSprite.visible = false;
				this.engine.inSceneUI.addChild(renderable.glowSprite);
			}

			//Set up trail sprites
			{
				const trailSprite = new PIXI.Sprite(depthTexture);
				trailSprite.anchor.set(0.5, 0.5);
				this.engine.background.addSprite(trailSprite);
				addComponent(entity, WaterTrail, { trailSprite }); //TODO: Think about this interface given new trail system

				//Set up cilia sprite
				const cilia = entity.getComponent(Cilia);
				if (cilia) {
					const base = this.spriteFactory.getCiliaSprites();
					base.alphaMode = PIXI.ALPHA_MODES.NPM;
					const location = cilia.location;
					const rect = new PIXI.Rectangle(location.x, location.y, location.width, location.height);
					const texture = new PIXI.Texture(base, rect);
					cilia.ciliaSprite = new PIXI.Sprite(texture);
					cilia.ciliaSprite.blendMode = PIXI.BLEND_MODES.NORMAL_NPM;
					cilia.ciliaSprite.anchor.set(0.5, 0.5);
					this.engine.cilia.addSprite(cilia.ciliaSprite);
				}

				entityTrace(entity, 'initialized sprite');
			}
		});

		//Move the light source back and forth on the x-axis
		this.engine.lightSourceOffsetX += delta / 1000 / 1.5;
		if (this.engine.lightSourceOffsetX > 1) this.engine.lightSourceOffsetX = -1;
		const offsetX = this.engine.lightSourceOffsetX > 0 ? this.engine.lightSourceOffsetX : -this.engine.lightSourceOffsetX;
		this.engine.lightSource.x = (Resolution.Width * (0.1 + (0.1 * offsetX)));

		//Mouse test
		// this.lightSource.xy = [mx, my];
		// console.log(`Light source: ${vecToStr(this.lightSource)}`);

		// Render All (TODO OPT, can't a lot of this be skipped if not OnScreen?)
		this.queries.renderables.results.forEach(entity => {
			const deltaSeconds = delta / 1000;
			const renderable = entity.getMutableComponent(Renderable);
			const transformComponent = entity.getComponent(Transform)
			const renderTransform = transformComponent.getRenderTransform();
			if (!renderTransform) return;

			//Hit stop, skip rendering entities for a couple frames on collisions to exaggerate impacts
			if (entity.hasComponent(Collision) && entity.hasComponent(Antibody) && entity.getComponent(Collision).collidedWith.some(col => col.canDamage)) {
				//if aready hitstopped, render this frame and start new hitstop
				if (renderable.hitstopSeconds > 0) {
					renderable.bypassHitstopThisFrame = true
				}
				renderable.hitstopSeconds = RenderEffects.HitStopDurationSeconds;
			}

			let hitstopActive = false;
			if (renderable.hitstopSeconds > 0) {
				renderable.hitstopSeconds -= deltaSeconds;

				//Check distance from where we stopped and end hitstop early
				const lastPos = new vec2([renderable.sprite.x, renderable.sprite.y]);
				const distance = vec2.difference(lastPos, renderTransform.position, lastPos).length();
				const limit = metersToPixels(Math.min(transformComponent.size.x, transformComponent.size.y)) / 2;
				if (distance > limit) {
					//Stop hitstop early and catchup
					renderable.hitstopSeconds = 0;
				}

				//Hitstop ended, start catchup
				if (renderable.hitstopSeconds <= 0) {
					renderable.hitstopSeconds = 0;
					renderable.hitstopCatchupPercent = 0; //start catchup
				}
				else {
					//Do hitstop. Unless we are interrupting it this frame
					if (!renderable.bypassHitstopThisFrame) {
						hitstopActive = true;
					}
				}
			}

			if (renderable.bypassHitstopThisFrame) renderable.bypassHitstopThisFrame = false;

			let { position, rotation, scale, radius } = renderTransform;

			if (renderable.hitstopCatchupPercent < 1) {
				const currentPos = new vec2([renderable.sprite.x, renderable.sprite.y]);
				const currentRot = renderable.sprite.rotation;
				lerp2D(currentPos, renderTransform.position, renderable.hitstopCatchupPercent, position);
				rotation = lerp(currentRot, renderTransform.rotation, renderable.hitstopCatchupPercent);
				renderable.hitstopCatchupPercent = Math.min(1, renderable.hitstopCatchupPercent + (deltaSeconds / RenderEffects.HitStopDurationSeconds));
			}

			//Move color
			if (!hitstopActive) {
				renderable.sprite.x = position.x;
				renderable.sprite.y = position.y;
				renderable.sprite.rotation = rotation;
			}
			renderable.sprite.scale.set(scale + renderable.addedScale.x, scale + renderable.addedScale.y);

			//Move depth
			if (!hitstopActive) {
				renderable.depthSprite.x = position.x;
				renderable.depthSprite.y = position.y;
				renderable.depthSprite.rotation = rotation;
			}
			renderable.depthSprite.scale.set(scale + renderable.addedScale.x, scale + renderable.addedScale.y);

			//Move membrane
			if (renderable.membrane) {
				if (!hitstopActive) {
					renderable.membrane.x = position.x;
					renderable.membrane.y = position.y;
					renderable.membrane.rotation = rotation;
				}
				renderable.membrane.scale.set(scale + renderable.addedScale.x, scale + renderable.addedScale.y);
			}

			if (!entity.hasComponent(Submerged)) {
				//renderable.trailScale = scale + renderable.addedScale.x;
			}

			//Expire end of history
			if (renderable.positionRadiusHistory.length > 1) {
				renderable.positionRadiusHistory.pop()
			}
			//Add to history if there are enough changes
			const scaledRadius = (renderable.trailScale * radius);
			//OPT: Unshift has linear time complexity, use push
			renderable.positionRadiusHistory.unshift({ position: transformComponent.position.copy(), radius: scaledRadius, rotation, time });

			//Turn on and tint the glow sprite as needed
			if (renderable.highlight !== HighlightType.None) {
				renderable.glowSprite.visible = true;
				renderable.glowSprite.position.set(renderable.sprite.x, renderable.sprite.y);
				renderable.glowSprite.rotation = rotation;
				const w = renderable.glowSprite.texture.width;
				const h = renderable.glowSprite.texture.height;
				const pad = 0.2 * renderable.glowSprite.texture.width; //0.2 is the padding in the glow texture, I guess ¯\_(ツ)_/¯ 
				const scaleUpW = (renderable.sprite.width + pad) / w;
				const scaleUpH = (renderable.sprite.height + pad) / h;
				renderable.glowSprite.scale.set(
					scaleUpW,
					scaleUpH
				);
				renderable.glowSprite.tint = renderable.highlight;
				//The default, can be overridden by other users of the glow sprite
				//eg the Flashing vfx 
				renderable.glowSprite.alpha = 0.3;
			}
			else {
				renderable.glowSprite.alpha = 0.3;
				renderable.glowSprite.visible = false;
			}

		});

		//If the cell has a cilia sprite track it to the same position as the cell
		this.queries.cilia.results.forEach(entity => {
			const cilia = entity.getComponent(Cilia);
			const renderable = entity.getComponent(Renderable);

			let hitstop = renderable.hitstopSeconds > 0 && renderable.hitstopCatchupPercent < 1;

			if (!hitstop) {
				cilia.ciliaSprite.x = renderable.depthSprite.x;
				cilia.ciliaSprite.y = renderable.depthSprite.y;
				cilia.ciliaSprite.rotation = renderable.depthSprite.rotation;
			}
			cilia.ciliaSprite.scale.set(renderable.depthSprite.scale.x, renderable.depthSprite.scale.y);

			let moving = false;
			const physics = entity.getComponent(Physics);
			if (physics) {
				moving = physics.body.GetLinearVelocity().LengthSquared() > 10;
			}
			const ciliaSpeed = moving ? 0.8 : 0.1;
			const hexSpeed = ciliaSpeed * 0x0000ff;
			cilia.ciliaSprite.tint = 0xffff00 + hexSpeed;
		});

		//Render water trails
		this.queries.waterTrail.removed?.forEach(entity => {
			const waterTrail = entity.getMutableComponent(WaterTrail) || entity.getRemovedComponent(WaterTrail);
			if (!waterTrail) return;
			waterTrail.trailSprite.visible = false;
		});

		const trailsToShow = new Array<Entity>();
		this.queries.waterTrail.results.forEach(entity => {
			//Update water trail
			const renderable = entity.getComponent(Renderable);
			const waterTrail = entity.getMutableComponent(WaterTrail);
			if (!waterTrail) return;
			if (renderable.positionRadiusHistory.length == 0) {
				waterTrail.trailSprite.visible = false;
			}
			else {
				const sprite = waterTrail.trailSprite;
				sprite.visible = true
				trailsToShow.push(entity);
			}
		});

		this.engine.background.updateTrailEntities(trailsToShow);

		//Animate dead things popping out of existence
		this.queries.dead.added?.forEach((entity) => {
			//Blood cells get handled differently because they dont really die, and cause full screen effects
			const owned = entity.hasComponent(Ownership) && entity.getComponent(Ownership).playerId === this.game.playerID;
			if (owned && !entity.hasComponent(HasSplit)) {
				const isBlood = entity.hasComponent(BloodCell);
				const isImmuneCell = entity.hasComponent(Antibody);
				if (isBlood || isImmuneCell) {
					this.engine.vignette.playBloodVisionEffect(0.5, isImmuneCell);
				}
				if (isBlood) return;
			}

			//Non blood cells..
			const renderable = entity.getComponent(Renderable);
			const colorSprite = renderable.sprite;
			const depthSprite = renderable.depthSprite;
			const glowSprite = renderable.glowSprite;
			const membrane = renderable.membrane;
			this.spriteReferencedForDeathAnimation.add(colorSprite);

			animate(depthSprite.scale, 'x', RenderEffects.DeathScaleSize + renderable.addedScale.x, RenderEffects.DeathScaleTime, easeOutCubic);
			animate(depthSprite.scale, 'y', RenderEffects.DeathScaleSize + renderable.addedScale.y, RenderEffects.DeathScaleTime, easeOutCubic);
			animate(colorSprite.scale, 'x', RenderEffects.DeathScaleSize + renderable.addedScale.x, RenderEffects.DeathScaleTime, easeOutCubic);
			animate(colorSprite.scale, 'y', RenderEffects.DeathScaleSize + renderable.addedScale.y, RenderEffects.DeathScaleTime, easeOutCubic).then(() => {
				if (this.spriteReferencedForDeathAnimation.has(colorSprite)) {
					this.spriteReferencedForDeathAnimation.delete(colorSprite);
					colorSprite.visible = false;
					depthSprite.visible = false;
					glowSprite.visible = false;
					if (membrane) membrane.visible = false;
					entityTrace(entity, "sprite hidden after death animation");
				}
				else {
					colorSprite.destroy();
					depthSprite.destroy();
					glowSprite.destroy();
					if (membrane) membrane.destroy();
					entityTrace(entity, "sprite destroyed after death animation");
				}
			});
		});

		this.queries.renderables.removed?.forEach(entity => {
			const renderable = entity.getRemovedComponent(Renderable);
			if (this.spriteReferencedForDeathAnimation.has(renderable.sprite)) {
				this.spriteReferencedForDeathAnimation.delete(renderable.sprite);
			}
			else {
				renderable.sprite.destroy();
				renderable.depthSprite.destroy();
				renderable.glowSprite.destroy();
				if (renderable.membrane) renderable.membrane.destroy();
				entityTrace(entity, "sprite destroyed immediately");
			}
			const trails = entity.getComponent(WaterTrail) || entity.getRemovedComponent(WaterTrail);
			if (trails !== undefined) trails.trailSprite.destroy();
		});

		this.queries.cilia.removed?.forEach(entity => {
			const cilia = entity.getComponent(Cilia) || entity.getRemovedComponent(Cilia);
			cilia.ciliaSprite.destroy();
		});

		if (this.displacementMap) {
			const waterMotion = time * 0.05;;
			this.displacementMap.x = waterMotion;
			this.displacementMap.y = waterMotion;
		}

		//Draw chain preview
		this.engine.graphicsOverlay.lineStyle(5, Colors.Chain);
		const movingChainEnds = this.queries.movingChainEnds.results
			.map(entity => entity.getComponent(Chained).links)
			.reduce((sources, moreSources) => sources.concat(moreSources), []);

		movingChainEnds.forEach(entity => {
			const chained = entity.getComponent(Chained);
			let sprPosition = entity.getComponent(Renderable).sprite.position;
			const dummVec = new vec2([sprPosition.x, sprPosition.y]);
			const position = this.engine.getScreenCoordinate(dummVec);

			chained.links.forEach(link => {
				if (!link.hasComponent(Renderable)) return;
				const target = link.getComponent(Renderable);
				const targetSprPos = target.sprite.position;
				dummVec.xy = [targetSprPos.x, targetSprPos.y];
				const targetPos = this.engine.getScreenCoordinate(dummVec);
				this.engine.graphicsOverlay.moveTo(position.x, position.y);
				this.engine.graphicsOverlay.lineTo(targetPos.x, targetPos.y);
			});
		});
		this.engine.graphicsOverlay.lineStyle(0);

		//Draw chains into membrane layer
		this.queries.chains.results?.forEach(entity => {
			const chains = entity.getComponent(Chained);
			const startT = entity.getComponent(Transform).getRenderTransform()
			chains.links.forEach((link, i) => {
				const endT = link.getComponent(Transform).getRenderTransform();
				this.engine.graphicsMembrane.lineStyle(Chaining.ChainWidthPixels, DefaultMembraneColor);
				this.engine.graphicsMembrane.moveTo(startT.position.x, startT.position.y);
				this.engine.graphicsMembrane.lineTo(endT.position.x, endT.position.y);
				this.engine.graphicsMembrane.drawCircle(endT.position.x, endT.position.y, Chaining.ChainWidthPixels / 4);
				this.engine.graphicsMembrane.endFill();
			});

		});

		//Blood effect
		// if (this.fullscreenBloodEffect > 0) {
		// 	this.engine.graphicsOverlay.beginFill(0xff0000, this.fullscreenBloodEffect);
		// 	this.engine.graphicsOverlay.lineStyle(0);
		// 	this.engine.graphicsOverlay.drawRect(0, 0, Resolution.Width, Resolution.Height);
		// 	this.engine.graphicsOverlay.endFill();
		// }

		//Work in progress..
		//Eating tendrils
		for (const entity of this.queries.consumer.results) {
			const consumer = entity.getComponent(Consumer);
			const consumerTransform = entity.getComponent(Transform);
			let side = 1;
			for (const consumeTarget of consumer.targets) {

				const consumable = consumeTarget.getComponent(Consumable);
				if (!consumable || !consumeTarget.hasComponent(Transform)) {
					console.warn('found invalid consumable in targets (grabber arms)');
					continue
				};

				const consumableTransform = consumeTarget.getComponent(Transform);
				const consumerRenderTransform = consumerTransform.getRenderTransform();
				const from = consumerRenderTransform.position
				const to = consumableTransform.getRenderTransform().position;
				const distance = vec2.distance(from, to);
				const reach = metersToPixels(consumer.range / 2);
				const grabDistance = reach / 2

				if (distance < reach) {
					const reachProgress = 1 - (Math.max(0, distance - grabDistance) / grabDistance)
					const reelProgress = Math.max(0, grabDistance - distance) / grabDistance;

					const lineToTarget = vec2.difference(to, from);
					const forward = lineToTarget.copy().normalize()
						.scale(consumerRenderTransform.radius * 0.8) //put it a little inside the cell
						.add(from);

					//Switches right or left based on side
					const start = rotateAround(forward, degToRad(90 * side), from);
					const startToTarget = vec2.difference(to, start);


					const cpAngles = reelProgress > 0 ? lerp(5, 45, reelProgress) : lerp(45, 5, reachProgress);
					const controlPoint1 = startToTarget.copy().scale(0.33).add(start);
					rotateAround(controlPoint1, degToRad(cpAngles), start, controlPoint1);
					const controlPoint2 = startToTarget.copy().scale(0.66).add(start);
					rotateAround(controlPoint2, degToRad(cpAngles), start, controlPoint2);

					const animatedStartToTarget = startToTarget.copy().scale(reachProgress);
					const end = animatedStartToTarget.copy().add(start);

					const adjStart = start;
					const adjEnd = end;
					const adjcp1 = controlPoint1;
					const adjcp2 = controlPoint2;

					this.engine.graphicsMembrane.beginFill(DefaultMembraneColor);
					this.engine.graphicsMembrane.lineStyle(6, DefaultMembraneColor, 1.0);
					this.engine.graphicsMembrane.moveTo(adjStart.x, adjStart.y);
					this.engine.graphicsMembrane.bezierCurveTo(adjcp1.x, adjcp1.y, adjcp2.x, adjcp2.y, adjEnd.x, adjEnd.y);
					this.engine.graphicsMembrane.drawCircle(adjEnd.x, adjEnd.y, 3);
					this.engine.graphicsMembrane.endFill();
				}
				side *= -1;
			}
		}

	}


}

// Define a query of entities that have "Renderable"
RendererSystem.queries = {
	renderables: {
		components: [Renderable], listen: {
			added: true,
			removed: true
		}
	},
	cilia: {
		components: [Renderable, Cilia], listen: { removed: true }
	},
	onscreen: {
		components: [Renderable, OnScreen],
		listen: { removed: true }
	},
	waterTrail: { components: [Renderable, WaterTrail], listen: { removed: true } },
	dead: { components: [Renderable, Dead], listen: { added: true } },
	chains: { components: [Not(Moved), Chainer, Chained, Renderable], listen: { added: true, removed: true } },
	movingChainEnds: { components: [Moved, OnScreen, Chained, Renderable], listen: { added: true, removed: true } },
	antigen: { components: [Renderable, OnScreen, Magnet] },
	consumer: { components: [Renderable, Transform, Consumer] },
};